import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

/**
 * Module-level timestamp of the last drag-end. Any click handler inside
 * a node can call `wasRecentlyDragged()` to suppress clicks that fire
 * as a side-effect of releasing a drag.
 */
let lastDragEndTime = 0;
export const wasRecentlyDragged = () => Date.now() - lastDragEndTime < 200;

type BadgeTone = 'neutral' | 'success' | 'failure';

const BADGE_TONE: Record<BadgeTone, string> = {
    neutral: 'border-border bg-background text-muted-foreground',
    success: 'border-success/30 bg-success/10 text-success',
    failure: 'border-destructive/30 bg-destructive/10 text-destructive',
};

function renderBadge(text: string, tone: BadgeTone) {
    return (
        <Badge
            variant="outline"
            className={cn('h-auto px-1.5 py-0 text-[10px] tracking-[0.02em]', BADGE_TONE[tone])}
        >
            {text}
        </Badge>
    );
}

interface NodeShellProps {
    x: number;
    y: number;
    width: number;
    height?: number;
    nodeId: string;
    selected?: boolean;
    onDrag?: (deltaX: number, deltaY: number) => void;
    /**
     * Fires on a no-drag mouse-up. Receives modifier flags so the
     * workspace can distinguish Ctrl/Cmd-click (multi-select toggle)
     * from plain click (replace selection).
     */
    onClick?: (modifiers: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }) => void;
    children: React.ReactNode;
    hoverAffordance?: React.ReactNode;
    /**
     * Like hoverAffordance, but receives a `close` callback so an action can
     * dismiss the "+" panel when it's picked (e.g. Add recipe → close the pills
     * before opening the recipe selector). Takes precedence over hoverAffordance
     * when addHandle is on.
     */
    renderActions?: (close: () => void) => React.ReactNode;
    /**
     * When true, the hoverAffordance is NOT shown on plain hover. Instead a
     * circular "+" handle appears on hover; clicking it reveals the affordance
     * (and later: drag from it to place). Keeps the old pill look, new trigger.
     */
    addHandle?: boolean;
    /**
     * When set, clicking the "+" handle fires this DIRECTLY instead of opening
     * the actions panel (used when there's a single primary action, e.g. the
     * product node opens the recipe picker straight away). Takes precedence
     * over renderActions / hoverAffordance for the click behaviour.
     */
    onAddClick?: () => void;
    /**
     * Keeps the "+" handle visible (and highlighted) while its options / picker
     * are open — e.g. the product node's recipe selector — so it doesn't vanish
     * when the pointer moves off the node onto the picker.
     */
    addOpen?: boolean;
    /**
     * Quick-action affordance revealed on hover BELOW the node, animating up
     * from the bottom edge (e.g. product node's Replace / Duplicate / Remove
     * circular buttons). Kept separate from the "+" panel above.
     */
    bottomAffordance?: React.ReactNode;
    /**
     * Right-click handler on the node root. Bubbles screen-space
     * coordinates. The parent is responsible for preventDefault /
     * stopPropagation — this prop just surfaces the raw event so a
     * per-node context menu can open at the click point.
     */
    onContextMenu?: (e: React.MouseEvent) => void;
    label?: string;
    labelBadge?: string;
    /**
     * Tone for the labelBadge — colors the pill background + border.
     * 'success' = green (ready), 'failure' = red, 'neutral' = default gray.
     */
    labelBadgeTone?: 'neutral' | 'success' | 'failure';
    /** Secondary badge (e.g. execution time) rendered right of labelBadge. */
    labelBadgeSecondary?: string;
    labelBadgeSecondaryTone?: 'neutral' | 'success' | 'failure';
    accent?: string;
    pulse?: boolean;
    zoom?: number;
    /**
     * Whether the node supports collapse/expand. Image and video
     * generation nodes opt out — collapsing an image to just its label
     * loses its primary content and makes the canvas harder to scan.
     * All other node types enable it by default.
     */
    collapsible?: boolean;
    /**
     * When true, fade the node back and block interactions. Used by
     * the workspace's multi-select mode to visually isolate the
     * asset-picker-eligible nodes without removing spatial context.
     */
    dimmed?: boolean;
}

/**
 * Reusable node frame. Uses the same drag pattern as IntegrationNode
 * (raw mouse events with delta / zoom compensation) — NOT framer-motion
 * drag, which fights with the parent canvas transform.
 *
 * Positioned with CSS left/top (no animation on position changes).
 * Framer-motion is used ONLY for:
 *   - Fade-in on mount (opacity)
 *   - Hover-revealed affordance slot (AnimatePresence)
 *   - Pulse ring for pending states
 */
export const NodeShell: React.FC<NodeShellProps> = ({
    x,
    y,
    width,
    height,
    nodeId,
    selected,
    onDrag,
    onClick,
    children,
    hoverAffordance,
    renderActions,
    addHandle,
    onAddClick,
    addOpen,
    bottomAffordance,
    onContextMenu,
    label,
    accent,
    labelBadge,
    labelBadgeTone = 'neutral',
    labelBadgeSecondary,
    labelBadgeSecondaryTone = 'neutral',
    pulse,
    zoom = 1,
    collapsible = true,
    dimmed = false,
}) => {
    const [hovered, setHovered] = useState(false);
    const [showAffordance, setShowAffordance] = useState(false);
    const hideTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    // Per-node collapse state — local because it's ephemeral UI (session-
    // scoped) and not worth persisting. Toggling shrinks the node body
    // down to a compact pill that only shows the label; the node stays
    // fully draggable. ResizeObserver in workspace picks up the new DOM
    // height so connection-line endpoints re-center automatically.
    const [collapsed, setCollapsed] = useState(false);
    // Whether the "+" handle has revealed the affordance (addHandle mode).
    const [actionsOpen, setActionsOpen] = useState(false);
    // True while the pointer is over the right-side "+" zone (vs the node body).
    // The bottom node buttons stay tied to the node itself, so hovering the
    // right zone shows ONLY the "+", not Replace/Duplicate/etc.
    const [overRightZone, setOverRightZone] = useState(false);
    const rootRef = useRef<HTMLDivElement | null>(null);
    // Close the revealed affordance on click-outside the node.
    useEffect(() => {
        if (!actionsOpen) return;
        const onDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                setActionsOpen(false);
            }
        };
        document.addEventListener('mousedown', onDown, true);
        return () => document.removeEventListener('mousedown', onDown, true);
    }, [actionsOpen]);
    const dragRef = useRef({
        startX: 0,
        startY: 0,
        hasMoved: false,
        ctrlKey: false,
        metaKey: false,
        shiftKey: false,
    });

    // Store callbacks and zoom in refs so the drag effect doesn't
    // re-register listeners on every render (inline arrows in JSX
    // change identity each render, which would break mid-drag).
    const onDragRef = useRef(onDrag);
    onDragRef.current = onDrag;
    const onClickRef = useRef(onClick);
    onClickRef.current = onClick;
    const zoomRef = useRef(zoom);
    zoomRef.current = zoom;
    const selectedRef = useRef(selected);
    selectedRef.current = selected;

    // The hover affordance (option pills) sits ~12px to the RIGHT of the node,
    // so crossing that gap fires overlapping leave/enter events. Route ALL
    // show/hide through these two helpers so a hide is always cancelled before
    // a new one is scheduled — otherwise two near-simultaneous leaves (node +
    // affordance) create two timers, one gets orphaned, and it hides the pills
    // even after the pointer returns to the node.
    const cancelHide = () => {
        if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
    };
    const scheduleHide = () => {
        cancelHide();
        // Generous grace so the "+" / pills don't vanish the instant the pointer
        // strays off the node while heading for them.
        hideTimerRef.current = setTimeout(() => setShowAffordance(false), 350);
    };

    // ── Mouse-based drag (mirrors IntegrationNode) ──────────────

    const handleMouseDown = (e: React.MouseEvent) => {
        // Right-click (button 2) belongs to the onContextMenu handler —
        // don't let drag take over, or the context menu opens with the
        // node in a dragging state and gets positioned awkwardly.
        if (e.button === 2) return;
        const target = e.target as HTMLElement;
        if (target.closest('button') || target.closest('textarea') || target.closest('input')) {
            e.stopPropagation();
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            hasMoved: false,
            ctrlKey: e.ctrlKey,
            metaKey: e.metaKey,
            shiftKey: e.shiftKey,
        };
    };

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            const deltaX = e.clientX - dragRef.current.startX;
            const deltaY = e.clientY - dragRef.current.startY;

            if (!dragRef.current.hasMoved && (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3)) {
                dragRef.current.hasMoved = true;
                // Select on drag start so the node is highlighted while dragging
                // and stays highlighted after release. Skip if it's already part
                // of the current selection so multi-node drags aren't collapsed.
                if (!selectedRef.current && onClickRef.current) {
                    onClickRef.current({ ctrlKey: false, metaKey: false, shiftKey: false });
                }
            }

            if (dragRef.current.hasMoved && onDragRef.current) {
                onDragRef.current(deltaX / zoomRef.current, deltaY / zoomRef.current);
                dragRef.current.startX = e.clientX;
                dragRef.current.startY = e.clientY;
            }
        };

        const handleMouseUp = () => {
            if (dragRef.current.hasMoved) {
                lastDragEndTime = Date.now();
                // Swallow the click that fires right after a drag so an option
                // row / tab under the cursor isn't "clicked" on release. Capture
                // phase + stopImmediatePropagation beats the child's onClick.
                const swallowClick = (e: MouseEvent) => {
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    e.preventDefault();
                    document.removeEventListener('click', swallowClick, true);
                };
                document.addEventListener('click', swallowClick, true);
                // Safety net: drop it if no click materialises.
                setTimeout(() => document.removeEventListener('click', swallowClick, true), 350);
            } else if (onClickRef.current) {
                onClickRef.current({
                    ctrlKey: dragRef.current.ctrlKey,
                    metaKey: dragRef.current.metaKey,
                    shiftKey: dragRef.current.shiftKey,
                });
            }
            setIsDragging(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]); // Only re-register when drag starts/stops

    const addActive = !!(actionsOpen || addOpen);

    return (
        <div
            ref={rootRef}
            data-cs-node
            data-cs-node-id={nodeId}
            onMouseDown={handleMouseDown}
            onContextMenu={onContextMenu}
            onMouseEnter={() => {
                setHovered(true);
                cancelHide();
                setShowAffordance(true);
            }}
            // onMouseOver bubbles and fires as the cursor moves between the
            // node's descendants (body ⇆ option pills), whereas onMouseEnter
            // does NOT re-fire when returning from a child. Cancelling the hide
            // here keeps the pills up as long as the cursor is anywhere within
            // the node or its pills — they only hide on the real onMouseLeave.
            onMouseOver={() => {
                cancelHide();
                if (!showAffordance) setShowAffordance(true);
            }}
            onMouseLeave={() => {
                setHovered(false);
                // Fires only when the pointer leaves the node AND all its
                // descendants (incl. the pills). Delay hiding slightly so a
                // quick re-entry keeps them visible.
                scheduleHide();
            }}
            className={cn(
                // Fade in on mount only — no position animation.
                'absolute animate-in fade-in duration-200 transition-opacity',
                dimmed
                    // Multi-select mode: non-eligible nodes fade back and stop
                    // receiving pointer events so clicks pass through to the
                    // workspace's selection handler.
                    ? 'pointer-events-none cursor-default opacity-[0.28]'
                    : isDragging
                    ? 'cursor-grabbing'
                    : 'cursor-grab',
            )}
            style={{
                left: x,
                top: y,
                width,
                height: height || 'auto',
                zIndex: isDragging ? 50 : hovered ? 10 : 1,
            }}
        >
            {/* Label row above the node — left group packs tight next to the
                label text, secondary badge is pinned flush with the card's
                right edge. Hidden when collapsed (the label lives inline
                inside the compact pill instead). */}
            {label && !collapsed && (
                <div className="pointer-events-none absolute -top-6 start-1 end-1 flex items-center justify-between gap-1.5">
                    <div className="flex min-w-0 items-center gap-1.5">
                        <div
                            title={label}
                            // Long labels (e.g. product names) ellipsize instead of
                            // overflowing past the node width. pointer-events for the title.
                            className="pointer-events-auto min-w-0 truncate text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                        >
                            {label}
                        </div>
                        {labelBadge && renderBadge(labelBadge, labelBadgeTone)}
                    </div>
                    {labelBadgeSecondary && renderBadge(labelBadgeSecondary, labelBadgeSecondaryTone)}
                </div>
            )}

            {/* The node body */}
            <div
                className={cn(
                    // The border stays a plain hairline even when selected — no dark
                    // outline. Selection reads through the ring instead.
                    'relative overflow-hidden rounded-xl border bg-card transition-all duration-200',
                    // Only transform WHILE dragging. A permanent `scale(1)` forces the
                    // node onto its own raster layer, which the compositor downsamples
                    // when the canvas zooms out → soft, pixelated edges.
                    isDragging && 'scale-[1.02] shadow-md',
                    selected && 'ring-1 ring-foreground/30',
                    hovered && !selected && !isDragging && 'border-input',
                )}
            >
                {/* Pulse ring for pending states */}
                {pulse && !collapsed && (
                    <motion.div
                        animate={{ opacity: [0.5, 0], scale: [1, 1.05] }}
                        transition={{
                            duration: 1.8,
                            repeat: Infinity,
                            ease: 'easeOut',
                        }}
                        className={cn(
                            'pointer-events-none absolute inset-0',
                            !accent && 'ring-2 ring-foreground/50',
                        )}
                        // `accent` is data — the node type's own colour — so it stays literal.
                        style={accent ? { boxShadow: `0 0 0 2px ${accent}` } : undefined}
                    />
                )}

                {collapsed ? (
                    // Compact pill view — just the label (+ optional
                    // status badge). Clicking anywhere on the pill (but
                    // not on the external toggle below) still drags,
                    // since the wrapping div owns the mouse-down handler
                    // at the shell level.
                    <div className="flex items-center gap-2 px-3 py-2 pe-7">
                        <div className="min-w-0 flex-1 truncate text-sm font-medium uppercase tracking-[0.04em] text-foreground">
                            {label || ' '}
                        </div>
                        {labelBadge && renderBadge(labelBadge, labelBadgeTone)}
                    </div>
                ) : (
                    children
                )}
            </div>

            {/* Collapse / expand toggle — a single circular chip anchored
                on the right edge at the node's vertical midpoint, right
                where connection lines exit. Half-out so it reads as the
                node's connector point rather than a floating control. */}
            {collapsible && (
                <button
                    type="button"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); setCollapsed((v) => !v); }}
                    aria-label={collapsed ? 'Expand' : 'Collapse'}
                    className="absolute -right-[11px] top-1/2 z-[4] flex size-[22px] -translate-y-1/2 items-center justify-center rounded-full border bg-card p-0 text-[10px] leading-none text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
                >
                    {collapsed ? '+' : '−'}
                </button>
            )}

            {/* "+" ADD HANDLE (addHandle mode) — shows on hover, or while the
                affordance is open, to the right of the node. Clicking toggles the
                affordance. Available even when the node is collapsed. */}
            {addHandle && (hoverAffordance || renderActions || onAddClick) && !isDragging && !dimmed && (
                <div
                    // ALWAYS-ON hover ZONE on the node's right side. Hovering it (even
                    // from the empty canvas to the right) reveals the "+" — it's a
                    // descendant, so onMouseOver flips showAffordance on. Also bridges
                    // the gap so the button is an easy, forgiving target. mousedown is
                    // swallowed so it never starts a node drag; z-3 keeps it under the
                    // collapse chip (z-4).
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseEnter={() => setOverRightZone(true)}
                    onMouseLeave={() => setOverRightZone(false)}
                    className="pointer-events-auto absolute top-0 bottom-0 z-[3] flex w-10 items-center pl-2"
                    style={{ left: width + 8 }}
                >
                    {(showAffordance || actionsOpen || addOpen) && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                // Single primary action → fire it directly; otherwise
                                // toggle the actions panel.
                                if (onAddClick) { onAddClick(); return; }
                                setActionsOpen((o) => !o);
                            }}
                            aria-label="Add"
                            className={cn(
                                'flex size-[26px] shrink-0 items-center justify-center rounded-full border bg-card p-0 transition-colors hover:border-foreground hover:text-foreground',
                                addActive ? 'border-foreground text-foreground' : 'text-muted-foreground',
                            )}
                            style={addActive && accent ? { borderColor: accent, color: accent } : undefined}
                        >
                            {/* Rotates into an "×" while the panel is open. */}
                            <Plus className={cn('size-3.5 transition-transform', actionsOpen && 'rotate-45')} />
                        </button>
                    )}
                </div>
            )}

            {/* Revealed affordance (pills). In addHandle mode it opens on the "+"
                click and sits to the right of the handle; otherwise it appears on
                plain hover flush beside the node (legacy path). */}
            <AnimatePresence>
                {(hoverAffordance || renderActions) && !isDragging
                    && (addHandle ? actionsOpen : (showAffordance && !collapsed)) && (
                    <div
                        className={cn(
                            'pointer-events-auto absolute top-0 bottom-0 flex items-center',
                            // legacy: a 12px hoverable bridge so there's no dead zone
                            // between the node and the pills.
                            !addHandle && 'pl-3',
                        )}
                        // addHandle: past the "+" handle; legacy: flush with the node.
                        style={{ left: addHandle ? width + 50 : width }}
                    >
                        <motion.div
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -8 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 32 }}
                            style={{ pointerEvents: 'auto' }}
                        >
                            {renderActions ? renderActions(() => setActionsOpen(false)) : hoverAffordance}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* BOTTOM AFFORDANCE — quick-action circular buttons revealed on hover
                below the node, sliding up from the bottom edge. Its container
                starts flush with the node bottom (with an 8px hoverable pad) so
                there's no dead zone between the node and the buttons. */}
            <AnimatePresence>
                {bottomAffordance && !isDragging && !collapsed && showAffordance && !overRightZone && (
                    <div className="pointer-events-auto absolute top-full left-0 z-[5] flex w-full justify-center pt-2">
                        <motion.div
                            // Emerge downward out of the node's bottom edge (start
                            // tucked up behind it, slide down into place).
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ type: 'spring', stiffness: 480, damping: 30 }}
                            style={{ pointerEvents: 'auto' }}
                        >
                            {bottomAffordance}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};
